import * as Yup from 'yup';
import { useApiGet, useApiPost, useApiPut } from '../../../../services/tanstackApiService';

// ---------------------------------------------------------------------
// Type definitions (moved from newPharmacy.types.ts)
// ---------------------------------------------------------------------

export interface Option {
  label: string;
  value: string;
}

export interface OptionforConssession {
  name: string;
  code: string;
  id:number;
  translation:null | string;
}

// Dynamic tax detail for rendering taxes from backend
export interface TaxDetail {
  name: string;
  percent: number;
  amount: number;
}

/**
 * Calculate taxes and net amount from tax-inclusive MRP.
 * 
 * Formula:
 * - Selling Price (without tax) = (Base MRP / (100 + Total Tax %)) * 100
 * - Total Selling Price = Selling Price * Quantity
 * - Individual Tax Amount = Total Selling Price * (Tax % / 100)
 * - Net Amount = Total Selling Price + Total Tax Amount
 */
export function calculateFromInclusiveMRP(
  baseMRP: number,
  qty: number,
  taxes: Array<{ name: string; percent: number; amount?: number }>
): {
  totalTaxPercent: number;
  sellingPricePerUnit: number;
  totalSellingPrice: number;
  taxesWithAmounts: Array<{ name: string; percent: number; amount: number }>;
  totalTaxAmount: number;
  netAmount: number;
} {
  // Step 1: Calculate total tax percentage
  const totalTaxPercent = taxes.reduce((sum, t) => sum + (t.percent || 0), 0);
  
  // Step 2: Calculate selling price per unit (price without tax)
  // SP = (MRP / (100 + Total Tax %)) * 100
  const sellingPricePerUnit = totalTaxPercent > 0 
    ? (baseMRP / (100 + totalTaxPercent)) * 100 
    : baseMRP;
  
  // Step 3: Calculate total selling price
  const totalSellingPrice = sellingPricePerUnit * qty;
  
  // Step 4: Calculate individual tax amounts
  const taxesWithAmounts = taxes.map(t => ({
    ...t,
    amount: Number(((totalSellingPrice * (t.percent || 0)) / 100).toFixed(2))
  }));
  
  // Total tax amount
  const totalTaxAmount = taxesWithAmounts.reduce((sum, t) => sum + t.amount, 0);
  
  // Step 5: Calculate net amount
  const netAmount = totalSellingPrice + totalTaxAmount;
  
  return {
    totalTaxPercent,
    sellingPricePerUnit: Number(sellingPricePerUnit.toFixed(2)),
    totalSellingPrice: Number(totalSellingPrice.toFixed(2)),
    taxesWithAmounts,
    totalTaxAmount: Number(totalTaxAmount.toFixed(2)),
    netAmount: Number(netAmount.toFixed(2))
  };
}

// ---------------------------------------------------------------------
// Table Styles - Shared across pharmacy tables
// ---------------------------------------------------------------------

/** Common scrollbar styles for table containers */
export const tableScrollbarStyles = {
  maxHeight: { xs: 320, md: 480 },
  overflowX: 'auto',
  '&::-webkit-scrollbar': {
    height: 8,
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#f8f9fa',
    borderRadius: 4,
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#cbd5e0',
    borderRadius: 4,
    '&:hover': {
      backgroundColor: '#a0aec0',
    },
  },
  scrollbarWidth: 'thin',
  scrollbarColor: '#cbd5e0 #f8f9fa',
};

/** Common header row styles for tables */
export const tableHeaderRowStyles = {
  position: 'sticky',
  top: 0,
  zIndex: 1,
  background: '#F5F6F7',
  '& .MuiTableCell-root': { borderBottom: 'none', background: '#F5F6F7' },
  whiteSpace: 'nowrap',
};

// ---------------------------------------------------------------------
// Utility Functions
// ---------------------------------------------------------------------

/** Check if any row has a quantity validation error */
export function hasAnyQtyError(items: PharmacyItem[]): boolean {
  return items.some(item => {
    const billedQtyNum = Number(item.billedQty) || 0;
    const prescribedQtyNum = Number(item.prescribed_remaining_qty) || Number(item.prescribedQty) || 0;
    const availableQtyNum = Number(item.availableStock);

    // Only enforce prescribed qty constraint for actual Rx items (not manually added / package items)
    const isPrescribedItem = (item.prescriptionId ?? 0) > 0 && !item.is_fromPackage && !item.packageId;
    if (isPrescribedItem && prescribedQtyNum > 0 && billedQtyNum > prescribedQtyNum) {
      return true;
    }
    
    // Check available stock constraint (applies to all items)
    if (!isNaN(availableQtyNum) && billedQtyNum > availableQtyNum) {
      return true;
    }
    
    // Note: Package amount constraint is NOT an error - exceeded qty will be
    // automatically split into non-package billing by splitItemsForBilling()
    
    return false;
  });
}

export interface AvailableBatchOption {
  batchId?: number;
  batchNumber: string;
  expiryDate: string;
  isExpired: boolean;
  baseMrp: number | null;
  baseCostPrice: number | null;
  /** @deprecated Use taxes array instead */
  sgstPercent?: number;
  /** @deprecated Use taxes array instead */
  cgstPercent?: number;
  /** @deprecated Use taxes array instead */
  igstPercent?: number;
  /** @deprecated Use taxes array instead */
  sgstAmount?: number;
  /** @deprecated Use taxes array instead */
  cgstAmount?: number;
  /** @deprecated Use taxes array instead */
  igstAmount?: number;
  /** Dynamic taxes array from backend */
  taxes?: TaxDetail[];
  availableStock?: number;
  availableUomId?: number | null;
  availableUom?: string;
  storeId?: number;
}

/** Frequency options for drug dosage (e.g., 1-0-1 = once in morning, once at night) */
export const FREQUENCY_OPTIONS = [
  '1-0-1',
  '1-1-1',
  '0-0-1',
  '0-1-1',
  '1-0-0',
  '1-1-0',
  '0-1-0',
  '1-1-1-1',
  '1-1-1-1-1',
  '2-2-2',
  '2-0-2',
  '1/2-0-1/2',
  '0-2-2',
  '2-2-0',
  '1-1-1-1-1-1',
  '1-2-1',
  '2-1-2',
  '0-0-2',
] as const;

export type FrequencyType = typeof FREQUENCY_OPTIONS[number] | '';

export interface PharmacyItem {
  /** Invoice item ID - for PUT requests when editing existing invoice */
  id?: number;
  prescriptionId?: number;
  itemId?: number;
  visitDate: string;
  itemCode: string;
  itemName: string;
  batchCode: string;
  batchId?: number;
  expiryDate: string;
  availableStock?: number;
  manufacturer: string;
  // Available batches for dropdown selection
  availableBatches: AvailableBatchOption[];
  // Prescribed values - READ ONLY (from prescription/previous order)
  prescribedStrength: string;
  prescribedQty: number;
  prescribedUOM: string;
  // Billed values - EDITABLE (actual billing)
  billedStrength: string;
  billedQty: number;
  billedUOM: string;
  frequency?: FrequencyType;
  rack: string;
  shelf: string;
  container: string;
  checked: boolean;
  // New fields from API
  baseMRP: number;
  baseCostPrice: number;
  totalMRPAmount: number;
  /** Selling price per unit (MRP without tax) */
  sellingPricePerUnit?: number;
  /** Total selling price = sellingPricePerUnit * qty */
  totalSellingPrice?: number;
  concessionAmount: number;
  concessionPercent: number;
  /** @deprecated Use taxes array instead */
  sgstAmount?: number;
  /** @deprecated Use taxes array instead */
  sgstPercent?: number;
  /** @deprecated Use taxes array instead */
  cgstAmount?: number;
  /** @deprecated Use taxes array instead */
  cgstPercent?: number;
  /** @deprecated Use taxes array instead */
  igstAmount?: number;
  /** @deprecated Use taxes array instead */
  igstPercent?: number;
  /** Dynamic taxes array from backend for rendering */
  taxes?: TaxDetail[];
  netAmount: number;
  pregnancyClass: string;
  provider: string;
  availableUomId?: number | null;
  availableUom?: string;
  storeId?: number;
  // Tariff and Package info
  tariffId?: number | null;
  tariffName?: string;
  is_fromPackage?: boolean;
  package_pharmacy_item_id?: number;
  
  packageId?: number | null;
  packageName?: string;
  package_total_qty?: number;
  package_pending_qty?: number;
  /** Remaining pharmacy amount available in the package */
  package_pharmacy_remaining_amount?: number;
  /** Unit price/MRP for calculating max units from remaining amount */
  package_unit_price?: number;
  // UOM conversions
  uomConversions?: UomConversion[];
  uom_description?: string;
  prescribed_remaining_qty?: number;
  // Package details from prescription for per-item package selection
  package_details?: PackageDetail[];
  /** Flag for items from dispense flow - concession should be disabled */
  is_from_dispense?: boolean;
}

export interface PharmacyFormValues {
  patientSource: string;
  tariff: string;
  packageId: string;
  items: PharmacyItem[];
  concessionReason: string;
  dueDate: string;
  termsAndConditions: string;
  legalMentions: string;
  remark: string;
  comments: string;
}

// Re-export package types and helpers from dedicated helper file
export {
  type Package,
  type PackageLevel,
  type PackageClassRate,
  type PackageRule,
  type PackagesApiResponse,
  type PackageOption,
  type UsePackagesOptions,
  usePackages,
  mapPackagesToOptions,
  getPackageRule,
  getPackageClassRate,
  getPackageLevel,
  isPackageValid,
  getPackageConcessionRange,
  getPackageDoctorShare,
  getPackageRateEditRange,
} from './packageSelect.helper';

// ---------------------------------------------------------------------
// Initial values & validation schema
// ---------------------------------------------------------------------

export const initialPharmacyValues: PharmacyFormValues = {
  patientSource: '',
  tariff: '',
  packageId: '',
  items: [],
  concessionReason: '',
  dueDate: '',
  termsAndConditions: '',
  legalMentions: '',
  remark: '',
  comments: '',
};

export const validationPharmacySchema = Yup.object().shape({
  patientSource: Yup.string().nullable(),
  tariff: Yup.string().nullable(),
  packageId: Yup.string().nullable(),
  items: Yup.array().of(
    Yup.object().shape({
      prescribedStrength: Yup.string().nullable(),
      // prescribedQty / prescribedUOM are only present on Rx items;
      // manually-added items have 0 / '' so we must not require them globally
      prescribedQty: Yup.number().nullable(),
      prescribedUOM: Yup.string().nullable(),
      billedStrength: Yup.string().nullable(),
      batchCode: Yup.string().when('checked', {
        is: true,
        then: (schema) => schema.required('Batch code is required'),  
        otherwise: (schema) => schema.nullable(),
      }),
      billedQty: Yup.number().when('checked', {
        is: true,
        then: (schema) => schema.min(1, 'Quantity must be at least 1').required('Billed quantity is required'),
        otherwise: (schema) => schema.nullable(),
      }),
      billedUOM: Yup.string().when('checked', {
        is: true,
        then: (schema) => schema.required('Billed UOM is required'),
        otherwise: (schema) => schema.nullable(),
      }),
    })
  ).min(1, 'At least one item is required'),

  concessionReason: Yup.string().test(
    'required-if-concession',
    'Concession reason is required when concession is applied',
    function (value) {
      const items = this.parent.items as PharmacyItem[];
      
      // Only require concession reason if a NON-PACKAGE item has concession applied
      // Package concessions are pre-defined and don't need a reason
      const hasNonPackageConcession = items?.some((item) => {
        if (!item.checked) return false;
        const isFromPackage = item.is_fromPackage === true || item.packageId != null;
        if (isFromPackage) return false; // Skip package items
        const concessionAmount = item.concessionAmount ?? 0;
        const concessionPercent = item.concessionPercent ?? 0;
        return concessionAmount > 0 || concessionPercent > 0;
      });
      
      // If there's a concession on non-package item, the reason is required
      if (hasNonPackageConcession) {
        return !!value && value.trim().length > 0;
      }
      
      // No non-package concession, so reason is not required
      return true;
    }
  ),
  dueDate: Yup.string().required(),
  termsAndConditions: Yup.string().nullable(),
  legalMentions: Yup.string().nullable(),
  remark: Yup.string().nullable(),
  comments: Yup.string().nullable(),
});

// ---------------------------------------------------------------------
// API response interfaces for Patient Source & Tariff
// ---------------------------------------------------------------------

export interface PatientSourceWithTariffs {
  label: string;
  value: string;
  tariffs: {
    id: number;
    tariff_id: number;
    tariff_name: string;
    tariff_code: string;
  }[];
}

export interface BillingConfigOptionsResponse {
  tariff_source_group: PatientSourceWithTariffs[];
  CONCESSION_REASON?: Array<{ id: number; reason_name: string; reason_code?: string }>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

// ---------------------------------------------------------------------
// Helper functions
// ---------------------------------------------------------------------


// ---------------------------------------------------------------------
// TanStack Query wrappers
// ---------------------------------------------------------------------

export function useBillingConfigOptions(patientId?: string | number) {
  return useApiGet<BillingConfigOptionsResponse>(
    ['billing', 'billing-config-options', patientId],
    {
      path: 'billing/billing-config-options/',
      query: { patient_id: patientId },
    },
    {
      enabled: !!patientId,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: 'always', // Refetch when navigating back to screen
      refetchOnReconnect: false,
    }
  );
}

// ---------------------------------------------------------------------
// TODO: Add hooks and helpers for future item management
// ---------------------------------------------------------------------

// TODO: Add useCreatePharmacy() hook
// TODO: Add useUpdatePharmacy() hook  
// TODO: Add usePharmacyDetails() hook
// TODO: Add item mapping helpers
// TODO: Add payment processing helpers


// API Response interfaces for existing patient prescriptions
export interface BatchTax {
  percent: number;
  amount: number;
}

export interface BatchTaxes {
  cgst: BatchTax;
  sgst: BatchTax;
  igst: BatchTax;
}

export interface AvailableBatch {
  id?: number;
  batch_id?: number;
  batch_number: string;
  expiry_date: string;
  is_expired: boolean;
  base_cost?: number;
  base_mrp: number | null;
  base_cost_price: number | null;
  available_stock?: number;
  available_uom_id?: number | null;
  available_uom?: string;
  available_uom_name?: string;
  available_uom_static_field?: string;
  conversion_factor?: number;
  store_id?: number;
  sgst_percent?: number;
  cgst_percent?: number;
  sgst_amount?: number;
  cgst_amount?: number;
  taxes?: BatchTaxes;
}

export interface UomConversion {
  id: number | null;
  item_id: number;
  item_name: string;
  uom_id: number;
  uom_code: string;
  uom?: string;
  code: string;
  uom_static_field: string;
  conversion_factor: number;
  converted_mrp: number;
  converted_cost_price: number;
}

export interface PackageDetail {
  package_id: number;
  package_name: string;
  package_pharmacy_item_id: number | null;
  item_id: number;
  item_name: string;
  item_code: string;
  total_quantity: number | null;
  quantity_consumed: number | null;
  quantity_remaining: number | null;
  uom_id: number | null;
  uom_code: string | null;
  uom_description: string | null;
  discount_percent: number;
  discount_amount_per_unit: number;
  total_discount_amount: number;
  unit_price: number;
  mrp: number;
  discounted_unit_price: number;
  pharmacy_amount: number;
  pharmacy_remaining_amount: number;
  is_set_all_items?: boolean;
}

export interface PrescriptionItem {
  /** Invoice item ID - present when fetching existing invoice details */
  id?: number;
  uom_object?: UomConversion[] | undefined;
  prescription_id: number;
  visit_date: string;
  item_id: number;
  item_code: string;
  item_name: string;
  manufacturer: string | null;
  pregnancy_class_id?: number | null;
  pregnancy_class_code?: string | null;
  available_batches: AvailableBatch[];
  batch_code: string | null;
  batch_id?: number;
  batch_number?: string;
  expiry_date: string | null;
  prescribed_strength: string | null;
  strength: string | null;
  prescribed_qty: number;
  prescribed_quantity?: number;
  prescribed_uom: string;
  remaining_qty: number;
  remaining_quantity?: number;
  billed_qty: number;
  billed_quantity?: number;
  billed_strength?: string;
  billed_uom: string;
  billed_uom_code?: string;
  frequency?: string;
  rx: boolean;
  prescribed_by: string;
  prescribed_by_id?: number;
  prescribed_on: string;
  rack_id: number | null;
  rack: string | null;
  shelf_id: number | null;
  shelf: string | null;
  container_id: number | null;
  container: string | null;
  available_stock?: number;
  available_uom_id?: number | null;
  available_uom?: string;
  uom_conversions?: UomConversion[];
  package_details?: PackageDetail[];
  is_fromPackage?: boolean;
  package_pharmacy_item_id?: number;
  package_id?: number;
  package_total_qty?: number;
  package_pending_qty?: number;
  sgst_percentage?: number;
  cgst_percentage?: number;
  sgst_amount?: number;
  cgst_amount?: number;
  sgst_percent_invoice?: number;
  cgst_percent_invoice?: number;
  sgst_amount_invoice?: number;
  cgst_amount_invoice?: number;
  total_mrp_amount?: number;
  // Additional fields for existing invoice details
  base_mrp?: number;
  unit_price?: number;
  base_cost_price?: number;
  concession_amount?: number;
  discount_amount?: number;
  concession_percentage?: number;
  net_amount?: number;
  pregnancy_class?: string | null;
  provider?: string;
  store_id?: number;
}

export interface PatientInfo {
  patient_name: string;
  age: number;
  sex: string;
  mrn: string;
  contact_no: string | null;
  treating_doctor: string;
  treating_doctor_id: number;
  visit_date: string;
  appointment_id: number;
  patient_id: number;
}

export interface ExistingPatientInvoiceResponse {
  status: boolean;
  message: string;
  data: {
    patient_info: PatientInfo;
    current_prescriptions: PrescriptionItem[];
    previous_prescriptions: PrescriptionItem[];
    metadata: {
      current_prescription_count: number;
      previous_prescription_count: number;
      generated_on: string;
    };
    store_id?: number | null;
    invoice_status?: string;
    package_details?: {
      package_id: number;
      package_name: string;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      [key: string]: any;
    };
  };
}


export interface ExistingPatientSelectedPrescriptionsResponse {
  status: boolean;
  message: string;
  items: PrescriptionItem[];
}


/**
 * Fetch existing patient prescriptions for pharmacy billing.
 *
 * This wraps:
 *   GET {baseUrl}/pharmacy-invoice/{appointmentId}/existing-patient-invoice-details/
 *
 * @param appointmentId - The appointment ID to fetch prescriptions for
 * @returns Query result with current and previous prescriptions
 */
export function useExistingPatientPrescriptions(appointmentId: number | undefined | null  , invoiceId?: number | null |undefined) {
  return useApiGet<ExistingPatientInvoiceResponse>(
    // queryKey: unique identifier for caching
    ['patientPrescriptions', appointmentId , invoiceId],
    // request config: dynamic path with appointmentId
    { path: `pharmacy-invoice/${appointmentId}/existing-patient-invoice-details/?invoice_id=${invoiceId}` },
    // react-query options
    {
      enabled: !!appointmentId || !!invoiceId, // Fetch when appointmentId OR invoiceId exists
    }
  );
}


export function useExistingPatientSelectedPrescriptions(itemIds: number[] | undefined, store_id?: number | null) {
  return useApiGet<ExistingPatientSelectedPrescriptionsResponse>(
    // queryKey: unique identifier for caching
    ['selectedPrescriptions', itemIds, store_id],
    // request config: dynamic path with query params
    { 
      path: 'pharmacy-dispensary/invoice-details-by-items/',
      query: { item_ids: itemIds?.join(','), store_id: store_id}
    },
    // react-query options
    {
      enabled: !!itemIds && itemIds.length > 0 && !!store_id, // Only fetch when itemIds exist and not empty

    }
  );
}

// ---------------------------------------------------------------------
// Create Pharmacy Invoice - POST /api/pharmacy-invoice/create-invoice/
// ---------------------------------------------------------------------

/**
 * Payload for creating a pharmacy invoice
 * Updated to match new API structure (Dec 27, 2025)
 */
export interface CreatePharmacyInvoiceItem {
  /** Invoice item ID - included in PUT requests for existing items */
  id?: number | null;
  item_id: number;
  item_code: string;
  item_name: string;
  prescription_id: number;
  prescribed_quantity: number;
  billed_quantity: number;
  remaining_quantity: number;
  unit_price: number;
  total_charges: number;
  discount_amount: number;
  discount_percent: number;
  tax_amount: number;
  net_amount: number;
  batch_number: string;
  batch_code: string;
  batch_id: number | null;
  expiry_date: string;
  visit_date: string;
  prescribed_uom: string;
  billed_uom: string;
  prescribed_dosage: string;
  prescribed_by: number | null;
  manufacturer: string;
  manufacturing_company_id: number | null;
  prescribed_strength: string;
  billed_strength: string;
  unit_of_measurement: string;
  base_mrp: number;
  total_mrp_amount: number;
  concession_amount: number;
  concession_percentage: number;
  taxes: {
    [key: string]: {
      percentage: number;
      amount: number;
    };
  };
  rack: string | null;
  rack_id: number | null;
  shelf: string | null;
  shelf_id: number | null;
  container: string | null;
  container_id: number | null;
  store_id: number | null;
  pregnancy_class: string | null;
  pregnancy_class_id: number | null;
  provider: string;
  provider_id: number | null;
  // Tariff and Package info
  tariff_id: number | null;
  tariff_name: string | null;
  package_id: number | null;
  package_name: string | null;
}

export interface CreatePharmacyInvoicePayload {
  patient_id: number;
  appointment_id: number;
  treatment_doctor_id: number;
  visit_date: string;
  bill_type: string;
  invoice_patient_type: string;
  remarks: string;
  total_amount: number;
  net_amount: number;
  concession_amount: number;
  tax_amount: number;
  balance_amount?: number;
  status?: string;
  is_from_dispense?: boolean;
  store_id?: number | null;
  prescription_items: CreatePharmacyInvoiceItem[];
  // Additional fields for complete invoice data
  concession_reason?: string;
  due_date?: string;
  legal_mentions?: string;
  comments?: string;
  terms_and_conditions?: string;
}

export interface CreatePharmacyInvoiceResponse {
  status: boolean;
  message: string;
  data?: {
    invoice_id: number;
    invoice_number: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
  };
}

/**
 * Build parameters for creating a pharmacy invoice.
 * 
 * @param body - The pharmacy invoice payload
 * @returns Object with path and body for the API call
 */
export function buildCreatePharmacyInvoiceParams(body: CreatePharmacyInvoicePayload) {
  return {
    path: 'pharmacy-invoice/create-invoice/',
    body,
  };
}
export function buildUpdatePharmacyInvoiceParams(invoiceId: number, body: CreatePharmacyInvoicePayload) {
  return {
    path: `invoices/${invoiceId}/`,
    body,
  };
}

/**
 * Hook to create a pharmacy invoice.
 * 
 * Usage:
 * ```tsx
 * const createInvoiceMutation = useCreatePharmacyInvoice();
 * 
 * const handleSubmit = async (values: PharmacyFormValues) => {
 *   const payload = mapFormToPayload(values);
 *   await createInvoiceMutation.mutateAsync(
 *     buildCreatePharmacyInvoiceParams(payload)
 *   );
 * };
 * ```
 * 
 * @returns TanStack Query mutation for creating pharmacy invoice
 */
export function useCreatePharmacyInvoice() {
  return useApiPost<CreatePharmacyInvoiceResponse, CreatePharmacyInvoicePayload>();
}
export function useUpdateInvoice() {
  return useApiPut<CreatePharmacyInvoiceResponse, CreatePharmacyInvoicePayload>();
}

/**
 * Maps form PharmacyItem to API CreatePharmacyInvoiceItem format
 * Only includes fields with actual values (not null/undefined/empty)
 */

/**
 * Builds taxes object for API payload in the format:
 * { "cgst": { "percentage": 2.5, "amount": 25.00 }, "sgst": { ... } }
 */
function buildTaxesObjectForApi(
  item: PharmacyItem,
  roundToTwo: (num: number) => number
): { [key: string]: { percentage: number; amount: number } } {
  const taxes: { [key: string]: { percentage: number; amount: number } } = {};
  
  // Check if item has dynamic taxes array
  if (item.taxes && item.taxes.length > 0) {
    item.taxes.forEach(tax => {
      if (tax.name && tax.percent > 0) {
        taxes[tax.name.toLowerCase()] = {
          percentage: tax.percent,
          amount: roundToTwo(tax.amount || 0),
        };
      }
    });
  } else {
    // Fallback to legacy individual tax fields
    if (item.sgstPercent && item.sgstPercent > 0) {
      taxes['sgst'] = {
        percentage: item.sgstPercent,
        amount: roundToTwo(item.sgstAmount || 0),
      };
    }
    if (item.cgstPercent && item.cgstPercent > 0) {
      taxes['cgst'] = {
        percentage: item.cgstPercent,
        amount: roundToTwo(item.cgstAmount || 0),
      };
    }
    if (item.igstPercent && item.igstPercent > 0) {
      taxes['igst'] = {
        percentage: item.igstPercent,
        amount: roundToTwo(item.igstAmount || 0),
      };
    }
  }
  
  return taxes;
}

export function mapFormItemToApiItem(item: PharmacyItem, visitDate: string, treatmentDoctorId: number): CreatePharmacyInvoiceItem {
  const remainingQuantity = item.prescribedQty - item.billedQty;
  
  // Convert expiry date from DD/MM/YYYY to YYYY-MM-DD format
  const convertDateFormat = (date: string): string => {
    if (!date) return '';
    const parts = date.split('/');
    if (parts.length === 3) {
      const [day, month, year] = parts;
      return `${year}-${month}-${day}`;
    }
    return date;
  };
  
  // Helper to get number value or null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getNumberOrNull = (value: any): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const num = Number(value);
    return isNaN(num) ? null : num;
  };
  
  // Helper to get string value or null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getStringOrNull = (value: any): string | null => {
    if (value === null || value === undefined || value === '') return null;
    return String(value);
  };
  
  /**
   * Properly round number to 2 decimal places for financial calculations
   * Uses Number.EPSILON to handle floating point precision edge cases
   */
  const roundToTwo = (num: number): number => {
    return parseFloat((Math.round((num + Number.EPSILON) * 100) / 100).toFixed(2));
  };
  return {
    id: item.id ?? null,
    item_id: item.itemId || 0,
    item_code: item.itemCode,
    item_name: item.itemName,
    prescription_id: item.prescriptionId || 0,
    prescribed_quantity: item.prescribedQty,
    billed_quantity: item.billedQty,
    remaining_quantity: remainingQuantity >= 0 ? remainingQuantity : 0,
    unit_price: roundToTwo(item.baseMRP),
    total_charges: roundToTwo(item.totalMRPAmount),
    discount_amount: roundToTwo(item.concessionAmount),
    discount_percent: roundToTwo(item.concessionPercent),
    tax_amount: roundToTwo((item.taxes || []).reduce((sum, t) => sum + (t.amount || 0), 0)),
    net_amount: roundToTwo(item.netAmount),
    batch_number: item.batchCode,
    batch_code: item.batchCode,
    batch_id: getNumberOrNull(item.batchId),
    expiry_date: convertDateFormat(item.expiryDate),
    visit_date: visitDate,
    prescribed_uom: item.prescribedUOM,
    billed_uom: item.billedUOM,
    prescribed_dosage: item.prescribedStrength || '0-0-0',
    prescribed_by: treatmentDoctorId,
    manufacturer: item.manufacturer,
    manufacturing_company_id: null,
    prescribed_strength: item.prescribedStrength,
    billed_strength: item.billedStrength,
    unit_of_measurement: item.billedUOM,
    base_mrp: roundToTwo(item.baseMRP),
    total_mrp_amount: roundToTwo(item.totalMRPAmount),
    concession_amount: roundToTwo(item.concessionAmount),
    concession_percentage: roundToTwo(item.concessionPercent),
    taxes: buildTaxesObjectForApi(item, roundToTwo),
    rack: getStringOrNull(item.rack),
    rack_id: getNumberOrNull(item.rack),
    shelf: getStringOrNull(item.shelf),
    shelf_id: getNumberOrNull(item.shelf),
    container: getStringOrNull(item.container),
    container_id: getNumberOrNull(item.container),
    store_id: getNumberOrNull(item.storeId),
    pregnancy_class: getStringOrNull(item.pregnancyClass),
    pregnancy_class_id: null,
    provider: item.provider,
    provider_id: getNumberOrNull(item.provider),
    // Tariff and Package info - only include if they have actual values
    tariff_id: getNumberOrNull(item.tariffId),
    tariff_name: getStringOrNull(item.tariffName),
    package_id: item.packageId !== null && item.packageId !== undefined ? Number(item.packageId) : null,
    package_name: item.packageName !== null && item.packageName !== undefined ? String(item.packageName) : null,
  };
}

/**
 * Tariff and Package info to be injected into items
 */
export interface TariffPackageInfo {
  tariffId?: number | null;
  tariffName?: string;
  packageId?: number | null;
  packageName?: string;
  storeId?: number | null;
}

/**
 * Maps PharmacyFormValues to CreatePharmacyInvoicePayload
 * Updated to match new API structure with required fields only
 */
export function mapFormToPharmacyInvoicePayload(
  values: PharmacyFormValues,
  patientId: number,
  appointmentId: number | undefined,
  treatmentDoctorId: number,
  visitDate: string,
  tariffPackageInfo?: TariffPackageInfo,
  status?: string,
  is_Dispense?: boolean,
  store_id?: number | null,
): CreatePharmacyInvoicePayload {
  
  // Filter only checked items and inject tariff/package info before mapping
  const selectedItems = values.items.filter((item) => item.checked);
  const apiItems: CreatePharmacyInvoiceItem[] = [];
  selectedItems.forEach((item) => {
    const pkgPendingQty = item.package_pending_qty ?? 0;
    const billedQty = item.billedQty ?? 0;
    const isFromPackage = !!item.is_fromPackage;

    // Scenario 1: Package pending qty is 0 or less - send without package_id
    if (pkgPendingQty <= 0 && (isFromPackage || item.packageId)) {
      const nonPkgItem: PharmacyItem = {
        ...item,
        tariffId: tariffPackageInfo?.tariffId ?? item.tariffId,
        tariffName: tariffPackageInfo?.tariffName ?? item.tariffName,
        storeId: tariffPackageInfo?.storeId ?? item.storeId,
        is_fromPackage: false,
        package_pharmacy_item_id: undefined,
        // Force package fields to null/undefined
        packageId: null,
        packageName: undefined,
        package_total_qty: undefined,
        package_pending_qty: undefined,
      };
      apiItems.push(mapFormItemToApiItem(nonPkgItem, visitDate, treatmentDoctorId));
    } 
    // Scenario 2: Input qty > package pending qty - split into two invoices
    else if (isFromPackage && pkgPendingQty > 0 && billedQty > pkgPendingQty) {
      // First invoice: with package_id for pending qty
      const pkgItem: PharmacyItem = {
        ...item,
        tariffId: tariffPackageInfo?.tariffId ?? item.tariffId,
        tariffName: tariffPackageInfo?.tariffName ?? item.tariffName,
        packageId: tariffPackageInfo?.packageId ?? item.packageId,
        packageName: tariffPackageInfo?.packageName ?? item.packageName,
        storeId: tariffPackageInfo?.storeId ?? item.storeId,
        billedQty: pkgPendingQty,
      };
      apiItems.push(mapFormItemToApiItem(pkgItem, visitDate, treatmentDoctorId));

      // Second invoice: without package_id for excess qty
      const nonPkgItem: PharmacyItem = {
        ...item,
        tariffId: tariffPackageInfo?.tariffId ?? item.tariffId,
        tariffName: tariffPackageInfo?.tariffName ?? item.tariffName,
        storeId: tariffPackageInfo?.storeId ?? item.storeId,
        billedQty: billedQty - pkgPendingQty,
        is_fromPackage: false,
        package_pharmacy_item_id: undefined,
        // Force package fields to null/undefined
        packageId: null,
        packageName: undefined,
        package_total_qty: undefined,
        package_pending_qty: undefined,
      };
      apiItems.push(mapFormItemToApiItem(nonPkgItem, visitDate, treatmentDoctorId));
    } 
    // Scenario 3: Normal case (input qty <= package pending qty) - send with package_id as is
    else {
      const itemWithTariffPackage: PharmacyItem = {
        ...item,
        tariffId: tariffPackageInfo?.tariffId ?? item.tariffId,
        tariffName: tariffPackageInfo?.tariffName ?? item.tariffName,
        packageId: tariffPackageInfo?.packageId ?? item.packageId,
        packageName: tariffPackageInfo?.packageName ?? item.packageName,
        storeId: tariffPackageInfo?.storeId ?? item.storeId,
      };
      apiItems.push(mapFormItemToApiItem(itemWithTariffPackage, visitDate, treatmentDoctorId));
    }
  });

  // Calculate totals from selected items
  const totals = selectedItems.reduce(
    (acc, item) => ({
      total: acc.total + item.totalMRPAmount,
      concession: acc.concession + item.concessionAmount,
      tax: acc.tax + (item.taxes || []).reduce((sum, t) => sum + (t.amount || 0), 0),
      net: acc.net + item.netAmount,
    }),
    { total: 0, concession: 0, tax: 0, net: 0 }
  );

  /**
   * Properly round number to 2 decimal places for financial calculations
   * Uses Number.EPSILON to handle floating point precision edge cases
   */
  const roundToTwo = (num: number): number => {
    return Math.round((num + Number.EPSILON) * 100) / 100;
  };

  /**
   * Format date to YYYY-MM-DD for API
   * Handles string dates, Date objects, and empty values
   */
  const formatDateForApi = (dateValue: string | Date | undefined | null): string => {
    if (!dateValue) return '';
    try {
      const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      // eslint-disable-next-line no-console
      console.warn('Date formatting error:', dateValue);
      return '';
    }
  };

  const remarks = values.remark ?? '';

  return {
    patient_id: patientId,
    appointment_id: appointmentId ?? 0, // Use 0 for dispense flow without appointment
    treatment_doctor_id: treatmentDoctorId,
    visit_date: visitDate, // Already in YYYY-MM-DD format from caller
    bill_type: 'PH',
    invoice_patient_type: 'REGULAR',
    remarks: remarks,
    total_amount: roundToTwo(totals.total),
    net_amount: roundToTwo(totals.net),
    concession_amount: roundToTwo(totals.concession),
    tax_amount: roundToTwo(totals.tax),
    prescription_items: apiItems,
    status: status || '',
    is_from_dispense: is_Dispense ?? false,
    store_id: store_id ?? null,
    // Include additional invoice details with proper date formatting
    concession_reason: values.concessionReason || '',
    due_date: formatDateForApi(values.dueDate),
    legal_mentions: values.legalMentions || '',
    comments: values.comments || '',
    terms_and_conditions: values.termsAndConditions || '',

  };
}